import { useState, useEffect, useRef } from "react";

// Global constant for consistent placeholder color for images (white)
export const PLACEHOLDER_COLOR = "#FFFFFF";

// Global constants for consistent fade behavior
export const FADE_DURATION = 400; // milliseconds
export const FADE_EASING = "ease-out";

/**
 * SiteImage - A shared image component with consistent loading behavior
 * 
 * Features:
 * - Shows a white placeholder initially
 * - Smoothly fades in the image once loaded
 * - Handles cached images correctly
 * - Respects reduced motion preferences
 * - Works reliably on mobile and slow connections
 */
export const SiteImage = ({
  src,
  alt = "",
  className = "",
  containerClassName = "",
  objectFit = "cover",
  aspectRatio = null, // e.g., "4/3", "16/9", "1/1"
  onClick = null,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  const prefersReducedMotion = useRef(
    typeof window !== "undefined" && 
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  // Reset state when src changes
  useEffect(() => {
    setIsLoaded(false);
    setHasError(false);
  }, [src]);

  // Handle cached images (already loaded before React attaches onLoad)
  useEffect(() => {
    if (imgRef.current && imgRef.current.complete && imgRef.current.naturalHeight !== 0) {
      setIsLoaded(true);
    }
  }, [src]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setHasError(true);
  };

  const transitionStyle = prefersReducedMotion.current 
    ? "none" 
    : `opacity ${FADE_DURATION}ms ${FADE_EASING}`;

  // Build aspect ratio style if provided
  const aspectStyle = aspectRatio ? { aspectRatio } : {};

  return (
    <div
      className={`relative overflow-hidden ${containerClassName}`}
      style={{
        backgroundColor: PLACEHOLDER_COLOR,
        ...aspectStyle,
      }}
      onClick={onClick}
    >
      {/* The image - starts invisible, fades in when loaded */}
      {src && !hasError && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          className={`w-full h-full ${className}`}
          style={{
            objectFit,
            opacity: isLoaded ? 1 : 0,
            transition: transitionStyle,
          }}
          {...props}
        />
      )}

      {/* Error state - shown if image fails to load */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <span className="text-gray-400 text-sm">Billede ikke tilgængeligt</span>
        </div>
      )}
    </div>
  );
};

/**
 * SiteBackgroundImage - For hero sections and full-cover backgrounds
 * Same behavior as SiteImage but renders as a background
 */
export const SiteBackgroundImage = ({
  src,
  alt = "",
  className = "",
  children,
  overlayClassName = "",
  style = {},
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const prefersReducedMotion = useRef(
    typeof window !== "undefined" && 
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  // Reset when src changes
  useEffect(() => {
    setIsLoaded(false);
    
    if (!src) return;

    // Preload the image
    const img = new Image();
    img.onload = () => {
      // Small delay to ensure smooth transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsLoaded(true);
        });
      });
    };
    img.src = src;

    return () => {
      img.onload = null;
    };
  }, [src]);

  const transitionStyle = prefersReducedMotion.current 
    ? "none" 
    : `opacity ${FADE_DURATION}ms ${FADE_EASING}`;

  // Extract backgroundPosition from style prop if provided
  const bgPosition = style.backgroundPosition || 'center center';

  return (
    <div className={`relative ${className}`} style={style} {...props}>
      {/* Green placeholder background - always visible */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{ backgroundColor: PLACEHOLDER_COLOR }}
      />
      
      {/* Image layer - fades in when loaded */}
      {src && (
        <div
          className="absolute inset-0 bg-cover pointer-events-none"
          style={{
            backgroundImage: `url('${src}')`,
            backgroundPosition: bgPosition,
            opacity: isLoaded ? 1 : 0,
            transition: transitionStyle,
          }}
          role="img"
          aria-label={alt}
        />
      )}
      
      {/* Optional overlay - only render if className provided */}
      {overlayClassName && (
        <div className={`absolute inset-0 pointer-events-none ${overlayClassName}`} />
      )}
      
      {/* Children content */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

export default SiteImage;
